import React, { useState, useEffect, useRef } from 'react';
import { Table, Textarea, Button, Box, Card, Group, Stack } from '@mantine/core';
import { IconX, IconGripVertical } from '@tabler/icons-react';
import { Experience } from '../../types';
import { useTranslation } from 'react-i18next';
import TextEditor from '../../../../components/TextEditor/TextEditor';
import { PROPOSALACTIONS } from '../../reducers/proposalReducer';
import { useProposal } from '../../context/ProposalContext';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import ExpandingTextArea from '../../../../components/ExpandingTextArea';

type ExperienceTableProps = {
  experiences: Experience[];
};

const ExperienceRow: React.FC<{
  experience: Experience;
  index: number;
  rowRefs: React.MutableRefObject<(HTMLTextAreaElement | null)[][]>;
  handleInputChange: (index: number, field: keyof Experience, value: string) => void;
  removeRow: (index: number) => void;
  children?: React.ReactNode;
}> = ({ experience, index, rowRefs, handleInputChange, removeRow, children }) => {
  const { t } = useTranslation();
  return (
    <Box style={{ padding: '1rem' }}>
      <Group justify="space-between" align="center">
        <Group gap="xs" align="center">
          <h3>{t('client')}</h3>
          {children}
        </Group>
        <Button variant="light" color="red" size="compact-xs" onClick={() => removeRow(index)}>
          <IconX size={16} />
        </Button>
      </Group>
      <Textarea
        ref={(el) => {
          rowRefs.current[index] = rowRefs.current[index] || [];
          rowRefs.current[index][2] = el;
        }}
        placeholder="Add client"
        value={experience.client}
        onChange={(event) => handleInputChange(index, 'client', event.currentTarget.value)}
        autosize
        style={{ resize: 'vertical' }}
      />
      <Box w="100%">
        <h3>{t('description')}</h3>
        <ExpandingTextArea
          value={experience.description || ''}
          placeholder={t('addDescription')}
          onChange={(event) => handleInputChange(index, 'description', event.target.value || '')}
          min_max_rows={6}
        />
      </Box>
    </Box>
  );
};

type SortableExperienceRowProps = {
  id: string;
  children: (dragHandleProps: { listeners: any; attributes: any }) => React.ReactNode;
};

export function SortableExperienceRow({ id, children }: SortableExperienceRowProps) {
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id,
  });

  const rowStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <Table.Tr ref={setNodeRef} style={rowStyle}>
      <Table.Td>
        <Card
          withBorder
          my="xs"
          style={{
            position: 'relative',
            padding: '1rem',
          }}
          w="100%"
        >
          {children({ listeners, attributes })}
        </Card>
      </Table.Td>
    </Table.Tr>
  );
}

const ExperienceTable: React.FC<ExperienceTableProps> = ({ experiences }) => {
  const rowRefs = useRef<(HTMLTextAreaElement | null)[][]>([]);
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();

  // Adds a new empty experience row
  const addRow = () => {
    dispatchProposal({ type: PROPOSALACTIONS.ADD_EXPERIENCE });
  };

  // Removes an experience row
  const removeRow = (index: number) => {
    dispatchProposal({ type: PROPOSALACTIONS.REMOVE_EXPERIENCE, payload: index });
  };

  // Updates the project, description, or client for a specific experience
  const handleInputChange = (index: number, field: keyof Experience, value: string) => {
    dispatchProposal({
      type: PROPOSALACTIONS.EDIT_EXPERIENCE,
      payload: { idx: index, [field]: value },
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = experiences.findIndex((exp) => exp.client === active.id);
    const newIndex = experiences.findIndex((exp) => exp.client === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reorderedExperiences = arrayMove(experiences, oldIndex, newIndex);
    dispatchProposal({
      type: PROPOSALACTIONS.REORDER_EXPERIENCES,
      payload: reorderedExperiences,
    });
  };

  return (
    <DndContext onDragEnd={handleDragEnd}>
      <SortableContext
        items={experiences.map((exp) => exp.client)}
        strategy={verticalListSortingStrategy}
      >
        <Table>
          <Table.Tbody>
            {Array.isArray(experiences) &&
              experiences.map((experience, index) => (
                <SortableExperienceRow key={experience.client} id={experience.client}>
                  {({ listeners, attributes }) => (
                    <ExperienceRow
                      experience={experience}
                      index={index}
                      rowRefs={rowRefs}
                      handleInputChange={handleInputChange}
                      removeRow={removeRow}
                    >
                      <div
                        style={{ cursor: 'grab', padding: '0.5rem' }}
                        {...listeners}
                        {...attributes}
                      >
                        <IconGripVertical size={20} />
                      </div>
                    </ExperienceRow>
                  )}
                </SortableExperienceRow>
              ))}
          </Table.Tbody>
        </Table>
      </SortableContext>
    </DndContext>
  );
};

export default ExperienceTable;
